import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email, origin: window.location.origin });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'envoi. Réessayez.");
    }
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center px-4 pt-24 pb-12">
      <Card className="w-full max-w-md p-8 shadow-lg" data-testid="forgot-password-card">
        <div className="text-center mb-6">
          <button onClick={() => navigate('/connexion')} className="inline-flex items-center mb-4 hover:opacity-80 transition-opacity">
            <img src="/logo-ler.png" alt="Location Benne Chantier" className="h-16 w-auto object-contain" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Mot de passe oublié</h1>
          <p className="text-gray-500 mt-2 text-sm">
            Entrez votre adresse email pour recevoir un lien de réinitialisation
          </p>
        </div>

        {sent ? (
          <div className="text-center space-y-4" data-testid="forgot-password-success">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Email envoyé !</h2>
            <p className="text-gray-500 text-sm">
              Si un compte existe avec l'adresse <strong>{email}</strong>, vous recevrez un email avec un lien de réinitialisation.
            </p>
            <p className="text-gray-400 text-xs">Pensez à vérifier vos spams.</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate('/connexion')}
              data-testid="back-to-login-btn"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour à la connexion
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-base font-semibold">Adresse email</Label>
              <Input
                id="email"
                type="email"
                className="mt-2 h-12"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.fr"
                required
                data-testid="forgot-password-email"
              />
            </div>

            {error && (
              <p className="text-red-500 text-sm bg-red-50 p-3 rounded-lg" data-testid="forgot-password-error">{error}</p>
            )}

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-bold"
              data-testid="forgot-password-submit"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-5 w-5" />
                  Envoyer le lien
                </>
              )}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/connexion')}
                className="text-sm text-green-600 hover:text-green-700 font-medium hover:underline"
                data-testid="back-to-login-link"
              >
                <ArrowLeft className="inline h-3.5 w-3.5 mr-1" />
                Retour à la connexion
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
};

export default ForgotPassword;
